/**
 * Selling-rate maths — what an hour of labour has to sell for.
 *
 * One dependency-free module shared by the Selling Rate page (live what-if) and
 * the server (the stored rate quoting can default to). No imports, no fetch, no
 * clock: everything it needs arrives in `inputs`, which api/_sellRate.js gathers
 * from QuickBooks (costs) and the timesheets (hours). Nothing here is typed in by
 * hand except the plan: a person's share of time on the tools, planned hires,
 * planned costs and the profit target.
 *
 * The model, per month:
 *   cost per job hour   = (field wages incl. their payroll costs + overhead) / hours clocked to jobs
 *   earned per job hour = (sales - materials, subs and permits at their marked-up price) / the same hours
 *   needed per job hour = (cost per job hour + pass-through cost per hour) / (1 - profit target)
 *                         - pass-through sold per hour
 *   van rate            = the rate the window's work was quoted at x needed / earned
 *
 * The last line is the important one. A clocked job hour does not turn into a
 * billed hour one for one (quoted jobs run over, travel is not billed, T&M sells
 * lower than a quoted van-hour), so the rate is scaled from what the current
 * rates actually bring in rather than from a perfect-world divide. The
 * perfect-world number is still returned as `van_fully_billed`.
 *
 * "Going forward" replaces the window's crew and overhead with today's: the
 * overhead running rate, the people active now at the hours they really work,
 * plus the plan.
 *
 * Every guess leans toward a HIGHER rate, because the costly mistake is telling
 * the owner he can charge less: the month that has just ended is left out while
 * its books are part-entered, a new starter or planned hire is paid in full but
 * counted at half the hours, a crew with more apprentice hours is taken to earn
 * less per hour, and the rate kept for quoting always uses 12 months.
 *
 * TODO(multi-tenant): nothing here is company-specific; the company's numbers
 * come in through `inputs` and its settings through `settings`.
 */

export const MAN_HOURS_PER_VAN_HOUR = 2;
export const HARD_MIN_VAN_RATE = 150;
export const HARD_MAX_VAN_RATE = 600;

export const SELL_RATE_DEFAULTS = {
  mode: "fixed",                 // "fixed" = the rate below; "calculator" = follow the calculated rate
  fixed_van_rate: 305,
  family_rates: {},              // job-type exceptions, e.g. { new_build: 280 }
  target_margin_pct: 20,         // profit before income tax, as a share of sales
  basis: "year",                 // which window the page shows first: "year" | "run". The rate kept for quoting always uses 12 months
  reference_rate_before_data: null, // the van rate work was quoted at in months with no quote data; null = unknown
  passthrough_markup_pct: 20,    // average markup on materials, subs and permits (an estimate)
  wear_monthly: null,            // vehicle and equipment wear; null = use what the books show
  apprentice_cost_ratio: 0.55,   // an apprentice hour costs this share of a journeyman hour
  std_month_hours: 171,          // a full month of paid hours for one person
  year_end_month: null,          // 1-12; null = take it from QuickBooks
  rounding_step: 5,
  floor: 250,
  ceiling: 450,
  max_step_per_month: 10,        // calculator mode: the default moves at most this much every 30 days (0 = no limit)
  people: {},                    // { "<jobber user id>": { tools_pct: 50 } } — share of a full month on jobs, 0-100
  planned: [],                   // [{ id, kind, label, trade, wage, hours_per_month, tools_pct, monthly_cost, enabled }]
};

export const PLANNED_KINDS = ["field_hire", "office_hire", "cost"];
// A new starter or a planned field hire is paid in full but counted at this share
// of the usual job hours until the timesheets show a full month.
export const NEW_HOURS_FACTOR = 0.5;
// Holiday and statutory pay on top of a planned hire's hourly wage (the existing
// crew's cost per hour, taken from the books, already includes it).
export const PLANNED_PAY_LOADING = 0.08;
export const LADDER_PCTS = [0, 5, 10, 15, 20, 25];

const isNum = (v) => typeof v === "number" && Number.isFinite(v);
const num = (v, d = 0) => { if (v === null || v === undefined || v === "") return d; const n = Number(v); return Number.isFinite(n) ? n : d; };
const sum = (a) => a.reduce((s, x) => s + x, 0);
const avg = (a) => (a.length ? sum(a) / a.length : 0);
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const r2 = (v) => (isNum(v) ? Math.round(v * 100) / 100 : null);

export function roundUpToStep(v, step) {
  const st = num(step, 5) > 0 ? num(step, 5) : 5;
  if (!isNum(v)) return null;
  // a hair of tolerance so 305.0000001 does not round to 310
  return Math.ceil((v - 1e-6) / st) * st;
}

// ── Account classification ─────────────────────────────────────────────────
// Only four categories change the answer: field wages (cost of sales), payroll
// costs (moved to sit with the wages they belong to), the income-tax provision
// (left out: the target is profit BEFORE tax) and amortisation (see `wear`).
// The rest only label the overhead breakdown.
export function classifyAccount(name, group) {
  const n = String(name || "").toLowerCase();
  if (group === "COGS") {
    if (/wage|labou?r|payroll|salar/.test(n)) return "wages";
    if (/sub-?contract|subtrade/.test(n)) return "subs";
    if (/permit|disposal|dump|inspection|freight/.test(n)) return "other_direct";
    return "materials";
  }
  if (/provision for .*tax|income tax|corporate tax/.test(n)) return "tax";
  if (/amorti[sz]|depreciat/.test(n)) return "wear";
  if (/\bcpp\b|\bei\b|employment insurance|work\s?safe|\bwcb\b|workers'? comp|employer contribution|employer health|\beht\b|payroll tax/.test(n)) return "burden";
  if (/salar|wage|payroll expens/.test(n) && !/processing|fee/.test(n)) return "office_wages";
  if (/advertis|marketing|promot|google ads|facebook|\bseo\b|website|sponsor/.test(n)) return "marketing";
  if (/vehicle|truck|\bvan\b|\bford\b|\bram\b|dodge|f-?150|f-?350|transit|\bfuel\b|trailer|parking|icbc/.test(n)) return "vehicles";
  return "other";
}

export const OVERHEAD_LABELS = {
  office_wages: "Office and owner pay",
  burden: "Payroll costs on office pay",
  marketing: "Advertising and marketing",
  vehicles: "Vehicles",
  wear: "Vehicle and equipment wear",
  other: "Everything else",
};

// ── Settings ───────────────────────────────────────────────────────────────
// Tolerant read: whatever is stored, hand back something the maths can run on.
export function normalizeSellRateSettings(raw) {
  const v = raw && typeof raw === "object" ? raw : {};
  const d = SELL_RATE_DEFAULTS;
  const pick = (k, lo, hi) => { const n = Number(v[k]); return Number.isFinite(n) && n >= lo && n <= hi ? n : d[k]; };
  const out = {
    mode: v.mode === "calculator" ? "calculator" : "fixed",
    fixed_van_rate: Math.round(pick("fixed_van_rate", HARD_MIN_VAN_RATE, HARD_MAX_VAN_RATE)),
    family_rates: {},
    target_margin_pct: pick("target_margin_pct", 0, 60),
    basis: v.basis === "run" ? "run" : "year",
    reference_rate_before_data: v.reference_rate_before_data === null || v.reference_rate_before_data === undefined || v.reference_rate_before_data === "" ? null
      : (Number.isFinite(Number(v.reference_rate_before_data)) && Number(v.reference_rate_before_data) >= HARD_MIN_VAN_RATE && Number(v.reference_rate_before_data) <= HARD_MAX_VAN_RATE ? Number(v.reference_rate_before_data) : null),
    passthrough_markup_pct: pick("passthrough_markup_pct", 0, 100),
    wear_monthly: v.wear_monthly === null || v.wear_monthly === undefined || v.wear_monthly === "" ? null
      : (Number.isFinite(Number(v.wear_monthly)) && Number(v.wear_monthly) >= 0 ? Number(v.wear_monthly) : null),
    apprentice_cost_ratio: pick("apprentice_cost_ratio", 0.2, 1),
    std_month_hours: pick("std_month_hours", 80, 260),
    year_end_month: Number.isInteger(Number(v.year_end_month)) && Number(v.year_end_month) >= 1 && Number(v.year_end_month) <= 12 ? Number(v.year_end_month) : null,
    rounding_step: [1, 5, 10].includes(Number(v.rounding_step)) ? Number(v.rounding_step) : d.rounding_step,
    floor: pick("floor", HARD_MIN_VAN_RATE, HARD_MAX_VAN_RATE),
    ceiling: pick("ceiling", HARD_MIN_VAN_RATE, HARD_MAX_VAN_RATE),
    max_step_per_month: pick("max_step_per_month", 0, 200),
    people: {},
    planned: [],
  };
  if (out.floor > out.ceiling) { out.floor = d.floor; out.ceiling = d.ceiling; }
  const fam = v.family_rates && typeof v.family_rates === "object" ? v.family_rates : {};
  for (const [k, val] of Object.entries(fam)) {
    const n = Number(val);
    if (/^[a-z0-9_]{1,40}$/.test(k) && Number.isFinite(n) && n >= HARD_MIN_VAN_RATE && n <= HARD_MAX_VAN_RATE) out.family_rates[k] = Math.round(n);
  }
  const ppl = v.people && typeof v.people === "object" ? v.people : {};
  for (const [id, p] of Object.entries(ppl)) {
    const n = Number(p && p.tools_pct);
    if (id && p && p.tools_pct !== null && p.tools_pct !== undefined && p.tools_pct !== "" && Number.isFinite(n) && n >= 0 && n <= 100) {
      out.people[String(id).slice(0, 120)] = { tools_pct: n };
    }
  }
  for (const p of Array.isArray(v.planned) ? v.planned : []) {
    if (!p || typeof p !== "object" || !PLANNED_KINDS.includes(p.kind)) continue;
    out.planned.push({
      id: String(p.id || `p${out.planned.length + 1}`).slice(0, 40),
      kind: p.kind,
      label: String(p.label || "").slice(0, 80),
      trade: p.trade === "apprentice" ? "apprentice" : "jman",
      wage: Math.max(0, num(p.wage)),
      hours_per_month: clamp(num(p.hours_per_month, d.std_month_hours), 0, 320),
      tools_pct: clamp(num(p.tools_pct, 95), 0, 100),
      monthly_cost: Math.max(0, num(p.monthly_cost)),
      enabled: p.enabled !== false,
    });
    if (out.planned.length >= 40) break;
  }
  return out;
}

// Strict check for a save: says what is wrong instead of quietly fixing it.
export function cleanSellRateSettings(input) {
  const v = input && typeof input === "object" ? input : {};
  const errors = [];
  const inRange = (label, val, lo, hi) => {
    const n = Number(val);
    if (val === "" || val === null || val === undefined || !Number.isFinite(n) || n < lo || n > hi) { errors.push(`${label} must be a number from ${lo} to ${hi}.`); return false; }
    return true;
  };
  if (v.mode !== "fixed" && v.mode !== "calculator") errors.push("Choose whether new quotes use a set rate or follow the calculator.");
  inRange("The set rate", v.fixed_van_rate, HARD_MIN_VAN_RATE, HARD_MAX_VAN_RATE);
  inRange("The profit target", v.target_margin_pct, 0, 60);
  inRange("The markup on materials", v.passthrough_markup_pct, 0, 100);
  const floorOk = inRange("The lowest rate allowed", v.floor, HARD_MIN_VAN_RATE, HARD_MAX_VAN_RATE);
  const ceilOk = inRange("The highest rate allowed", v.ceiling, HARD_MIN_VAN_RATE, HARD_MAX_VAN_RATE);
  if (floorOk && ceilOk && Number(v.floor) > Number(v.ceiling)) errors.push("The lowest rate allowed is above the highest.");
  if (![1, 5, 10].includes(Number(v.rounding_step))) errors.push("Round the rate to the nearest $1, $5 or $10.");
  inRange("The most the rate may move in a month", v.max_step_per_month, 0, 200);
  inRange("A full month of hours", v.std_month_hours, 80, 260);
  inRange("The apprentice cost share", v.apprentice_cost_ratio, 0.2, 1);
  if (!(v.wear_monthly === null || v.wear_monthly === undefined || v.wear_monthly === "")) inRange("Vehicle and equipment wear", v.wear_monthly, 0, 100000);
  if (!(v.reference_rate_before_data === null || v.reference_rate_before_data === undefined || v.reference_rate_before_data === "")) inRange("The rate earlier work was quoted at", v.reference_rate_before_data, HARD_MIN_VAN_RATE, HARD_MAX_VAN_RATE);
  if (!(v.year_end_month === null || v.year_end_month === undefined || v.year_end_month === "")) {
    if (!Number.isInteger(Number(v.year_end_month)) || Number(v.year_end_month) < 1 || Number(v.year_end_month) > 12) errors.push("The year-end month must be 1 to 12.");
  }
  const fam = v.family_rates && typeof v.family_rates === "object" ? v.family_rates : {};
  for (const [k, val] of Object.entries(fam)) {
    if (!/^[a-z0-9_]{1,40}$/.test(k)) errors.push(`"${k}" is not a job type.`);
    else inRange(`The rate for ${k.replace(/_/g, " ")}`, val, HARD_MIN_VAN_RATE, HARD_MAX_VAN_RATE);
  }
  const ppl = v.people && typeof v.people === "object" ? v.people : {};
  for (const p of Object.values(ppl)) {
    if (p && !(p.tools_pct === null || p.tools_pct === undefined || p.tools_pct === "")) inRange("A person's share of time on the tools", p.tools_pct, 0, 100);
  }
  for (const p of Array.isArray(v.planned) ? v.planned : []) {
    if (!p || !PLANNED_KINDS.includes(p.kind)) { errors.push("A planned change has no type."); continue; }
    const what = String(p.label || "A planned change").slice(0, 60);
    if (!String(p.label || "").trim()) errors.push("Every planned change needs a name.");
    if (p.kind === "field_hire") {
      inRange(`${what}: hourly wage`, p.wage, 1, 300);
      inRange(`${what}: hours a month`, p.hours_per_month, 1, 320);
      inRange(`${what}: share of time on the tools`, p.tools_pct, 0, 100);
      if (!(p.monthly_cost === "" || p.monthly_cost === null || p.monthly_cost === undefined)) inRange(`${what}: other monthly cost`, p.monthly_cost, 0, 100000);
    } else {
      inRange(`${what}: monthly cost`, p.monthly_cost, 1, 250000);
    }
  }
  if (errors.length) return { ok: false, errors, settings: null };
  return { ok: true, errors: [], settings: normalizeSellRateSettings(v) };
}

// ── In-force rate (calculator mode) ────────────────────────────────────────
// The default new quotes open at follows the calculated rate, but never by more
// than `max_step_per_month` every 30 days: sales lag quotes by weeks, so right
// after a rate change the calculation still reads the old prices and would keep
// climbing if it were followed at once.
function dayNumber(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ""));
  return m ? Math.floor(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / 86400000) : null;
}
export function candidateVanRate(rawRate, settings) {
  const s = normalizeSellRateSettings(settings);
  if (!isNum(rawRate) || rawRate <= 0) return null;
  const lo = Math.max(s.floor, HARD_MIN_VAN_RATE), hi = Math.min(s.ceiling, HARD_MAX_VAN_RATE);
  return clamp(roundUpToStep(rawRate, s.rounding_step), lo, hi);
}
export function nextInForce(prev, candidate, settings, today) {
  const s = normalizeSellRateSettings(settings);
  if (!isNum(candidate)) return prev && isNum(prev.van_rate) ? prev : null;
  const lo = Math.max(s.floor, HARD_MIN_VAN_RATE), hi = Math.min(s.ceiling, HARD_MAX_VAN_RATE);
  const has = prev && isNum(prev.van_rate);
  const from = clamp(has ? prev.van_rate : s.fixed_van_rate, lo, hi);
  if (!s.max_step_per_month) return { van_rate: candidate, since: today, target: candidate };
  if (candidate === from) return { van_rate: from, since: has ? (prev.since || today) : today, target: candidate };
  const t = dayNumber(today), since = has ? dayNumber(prev.since) : null;
  const mayMove = !has || since === null || t === null || t - since >= 30;
  if (!mayMove) return { van_rate: from, since: prev.since, target: candidate };
  const step = Math.min(Math.abs(candidate - from), s.max_step_per_month);
  const moved = from + Math.sign(candidate - from) * step;
  return { van_rate: clamp(moved, lo, hi), since: today, target: candidate };
}

// ── Hours helpers ──────────────────────────────────────────────────────────
// people[].m = { "YYYY-MM": [paidHours, jobHours, requestHours, daysClocked] }
const P_PAID = 0, P_JOB = 1, P_DAYS = 3;
const cell = (p, ym, i) => num(p && p.m && p.m[ym] && p.m[ym][i]);
const isFieldTrade = (t) => t === "jman" || t === "apprentice";

function monthTotals(people, months) {
  const out = {};
  for (const ym of months) {
    let job = 0, paid = 0;
    for (const p of people) { job += cell(p, ym, P_JOB); paid += cell(p, ym, P_PAID); }
    out[ym] = { job, paid };
  }
  return out;
}
// Is this person's pay in the field-wage account? Field trades (and ex-staff with
// no title who clocked job time), as long as most of their clocked time was on
// jobs. An owner who is in the office nearly all month is paid from overhead.
function inWageBase(p, months) {
  let paid = 0, job = 0;
  for (const ym of months) { paid += cell(p, ym, P_PAID); job += cell(p, ym, P_JOB); }
  if (paid <= 0) return false;
  if (p.trade === "office") return false;
  if (!isFieldTrade(p.trade) && job <= 0) return false;
  return job / paid >= 0.5;
}
const tradeWeight = (trade, k) => (trade === "jman" ? 1 : trade === "apprentice" ? k : (1 + k) / 2);
const median = (arr) => { const t = [...arr].sort((x, y) => x - y); return t.length ? (t.length % 2 ? t[(t.length - 1) / 2] : (t[t.length / 2 - 1] + t[t.length / 2]) / 2) : null; };
const dollars = (v) => `$${Math.round(v).toLocaleString("en-CA")}`;
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const monthName = (ym) => { const n = MONTH_NAMES[Number(String(ym).slice(5, 7)) - 1]; return n ? `${n} ${String(ym).slice(0, 4)}` : String(ym); };

// ── The calculation ────────────────────────────────────────────────────────
export function computeSellRate(inputs, rawSettings, opts = {}) {
  const s = normalizeSellRateSettings(rawSettings);
  const basis = opts.basis === "run" || opts.basis === "year" ? opts.basis : s.basis;
  const targetPct = isNum(opts.target_margin_pct) ? clamp(opts.target_margin_pct, 0, 60) : s.target_margin_pct;
  const mk = s.passthrough_markup_pct / 100;
  const k = s.apprentice_cost_ratio;
  const std = s.std_month_hours;
  const warnings = [...((inputs && inputs.warnings) || [])];
  const assumptions = [];
  const fail = (msg) => ({ ok: false, errors: [msg], warnings, assumptions });

  const plMonths = (inputs && inputs.months) || [];
  const pl = (inputs && inputs.pl) || {};
  const accounts = pl.accounts || [];
  const revenueByMonth = pl.revenue || [];
  const hours = (inputs && inputs.hours) || {};
  const people = hours.people || [];
  const hourMonths = hours.months || [];
  const currentMonth = inputs && inputs.current_month;
  const asOf = inputs && inputs.as_of;
  if (!plMonths.length) return fail("No cost data: QuickBooks returned no complete months.");
  if (!people.length) return fail("No timesheet hours were found.");

  const idx = {}; plMonths.forEach((ym, i) => { idx[ym] = i; });
  const totals = monthTotals(people, hourMonths);
  const OVERHEAD_GROUPS = ["Expenses", "OtherExpenses"];
  const overheadAccounts = accounts.filter((a) => OVERHEAD_GROUPS.includes(a.group));
  const cogsAccounts = accounts.filter((a) => a.group === "COGS");
  const monthSum = (list, ym) => sum(list.map((a) => num(a.v && a.v[idx[ym]])));

  // Months the sums may use: complete, with books and with timesheet hours.
  const missing = new Set(pl.missing_months || []);
  let usable = plMonths.filter((ym) => ym !== currentMonth && !missing.has(ym) && totals[ym] && totals[ym].job > 0);
  // The month that has just ended is often only part-entered (supplier bills and
  // card charges land for weeks). Counting it as complete makes costs look low and
  // the rate with them, so it is left out until its books look like a normal month.
  if (usable.length >= 5) {
    const newest = usable[usable.length - 1];
    const prior = usable.slice(-7, -1);
    const rev = num(revenueByMonth[idx[newest]]);
    const overheadShare = monthSum(overheadAccounts, newest) / (median(prior.map((ym) => monthSum(overheadAccounts, ym))) || 1);
    const ratio = (ym) => { const r = num(revenueByMonth[idx[ym]]); return r > 0 ? monthSum(cogsAccounts, ym) / r : null; };
    const priorRatios = prior.map(ratio).filter((x) => x !== null);
    const cogsShare = rev > 0 && priorRatios.length ? ratio(newest) / (median(priorRatios) || 1) : 1;
    if (overheadShare < 0.7 || cogsShare < 0.7) {
      usable = usable.slice(0, -1);
      warnings.push(`The books for ${monthName(newest)} look part-entered (${overheadShare < 0.7 ? `overhead is ${Math.round(overheadShare * 100)}% of a normal month` : `job costs are ${Math.round(cogsShare * 100)}% of their usual share of sales`}), so that month is left out until they catch up.`);
    }
  }
  if (missing.size) warnings.push(`No books were found for ${[...missing].slice(0, 4).map(monthName).join(", ")}${missing.size > 4 ? " and others" : ""}; those months are left out.`);
  if (usable.length < 3) return fail("Fewer than three complete months have both costs and timesheet hours.");

  const yearEnd = s.year_end_month || num(inputs && inputs.hints && inputs.hints.year_end_month) || null;
  const yearWin = usable.slice(-12);
  const runWin = [];
  for (let i = usable.length - 1; i >= 0 && runWin.length < 3; i--) {
    // the year-end month carries one-off entries; a three-month window must not hold it
    if (yearEnd && Number(usable[i].slice(5, 7)) === yearEnd) continue;
    runWin.unshift(usable[i]);
  }
  if (runWin.length < 3) return fail("Not enough recent months outside the year-end month.");
  if (yearWin.length < 12) warnings.push(`Only ${yearWin.length} complete months have both costs and hours, so the 12-month view covers those.`);
  if (pl.reconciled === false) warnings.push("The account lines did not add up to QuickBooks' own section totals in at least one month; the totals here may be slightly off.");

  const acctAvg = (a, win) => avg(win.map((ym) => num(a.v && a.v[idx[ym]])));
  const catAvg = (cat, win, groups) => sum(accounts.filter((a) => a.cat === cat && (!groups || groups.includes(a.group))).map((a) => acctAvg(a, win)));

  // Payroll costs (CPP, EI, WorkSafe, health) as a share of all wages, on the
  // longest window: WorkSafe posts quarterly, so three months misread it.
  const wagesYear = catAvg("wages", yearWin, ["COGS"]);
  const officeYear = catAvg("office_wages", yearWin, OVERHEAD_GROUPS);
  const burdenYear = catAvg("burden", yearWin, OVERHEAD_GROUPS);
  const burdenRate = wagesYear + officeYear > 0 ? burdenYear / (wagesYear + officeYear) : 0;

  // Vehicle and equipment wear. If the books carry none for the year, owned
  // vehicles would be counted as free.
  const wearBookedYear = catAvg("wear", yearWin, OVERHEAD_GROUPS);
  const lastBooked = inputs && inputs.hints && inputs.hints.amortisation_last;
  let wearMonthly, wearSource;
  if (isNum(s.wear_monthly)) { wearMonthly = s.wear_monthly; wearSource = "set"; }
  else if (Math.abs(wearBookedYear) >= 1) { wearMonthly = wearBookedYear; wearSource = "books"; }
  else if (lastBooked && num(lastBooked.amount) > 0) { wearMonthly = num(lastBooked.amount) / 12; wearSource = "last_booked"; }
  else { wearMonthly = 0; wearSource = "none"; }
  if (wearSource === "last_booked") assumptions.push(`QuickBooks has booked no vehicle or equipment wear in the last 12 months. Using ${dollars(wearMonthly)} a month, which is what was last booked (the 12 months to ${monthName(lastBooked.ym)}) spread evenly. Vehicles bought since then are not in that figure.`);
  if (wearSource === "none") warnings.push("QuickBooks has booked no vehicle or equipment wear, so owned vehicles count as free here. Enter a monthly amount under Assumptions.");

  // What an apprentice hour earns next to a journeyman hour, from the T&M rates.
  const tm = (inputs && inputs.rates && inputs.rates.tm) || {};
  const earnApp = isNum(tm.jman) && isNum(tm.app) && tm.jman > 0 ? clamp(tm.app / tm.jman, 0.3, 1) : 0.68;
  const earnWeight = (trade) => (trade === "jman" ? 1 : trade === "apprentice" ? earnApp : (1 + earnApp) / 2);

  // One window of actuals, as monthly averages.
  function actualFor(win) {
    const revenue = avg(win.map((ym) => num(revenueByMonth[idx[ym]])));
    const wages = catAvg("wages", win, ["COGS"]);
    const materials = catAvg("materials", win, ["COGS"]);
    const subs = catAvg("subs", win, ["COGS"]);
    const otherDirect = catAvg("other_direct", win, ["COGS"]);
    const passthrough = materials + subs + otherDirect;
    const tax = catAvg("tax", win, OVERHEAD_GROUPS);
    const wearBooked = catAvg("wear", win, OVERHEAD_GROUPS);
    const expensesBooks = sum(overheadAccounts.map((a) => acctAvg(a, win)));
    const burdenField = burdenRate * wages;
    const wagesLoaded = wages + burdenField;
    // overhead = booked expenses, less the tax provision, less the payroll costs that
    // belong to field wages, with wear set to the normal monthly figure
    const overhead = expensesBooks - tax - wearBooked - burdenField + wearMonthly;
    const jobHours = avg(win.map((ym) => totals[ym].job));
    const base = people.filter((p) => inWageBase(p, win));
    const paidWeighted = avg(win.map((ym) => sum(base.map((p) => cell(p, ym, P_PAID) * tradeWeight(p.trade, k)))));
    // how much of the window's job hours were apprentice hours, weighted by what each trade's hour earns
    let jobAll = 0, jobEarn = 0, jobApp = 0;
    for (const p of people) for (const ym of win) { const h = cell(p, ym, P_JOB); jobAll += h; jobEarn += h * earnWeight(p.trade); if (p.trade === "apprentice") jobApp += h; }
    const net = revenue - passthrough - wagesLoaded - overhead;
    return {
      months: win, revenue, wages_books: wages, burden_field: burdenField, wages_loaded: wagesLoaded,
      materials, subs, other_direct: otherDirect, passthrough,
      expenses_books: expensesBooks, tax_excluded: tax, wear_booked: wearBooked, wear_used: wearMonthly, overhead,
      job_hours: jobHours, paid_weighted: paidWeighted,
      earn_mix: jobAll > 0 ? jobEarn / jobAll : 1, apprentice_share_pct: jobAll > 0 ? (jobApp / jobAll) * 100 : 0,
      wage_per_job_hour: jobHours > 0 ? wagesLoaded / jobHours : null,
      overhead_per_job_hour: jobHours > 0 ? overhead / jobHours : null,
      cost_per_job_hour: jobHours > 0 ? (wagesLoaded + overhead) / jobHours : null,
      passthrough_per_job_hour: jobHours > 0 ? passthrough / jobHours : null,
      earned_per_job_hour: jobHours > 0 ? (revenue - passthrough * (1 + mk)) / jobHours : null,
      net, net_margin_pct: revenue > 0 ? (net / revenue) * 100 : null,
      books_net: revenue - (wages + passthrough) - expensesBooks,
    };
  }
  const year = actualFor(yearWin);
  const run = actualFor(runWin);
  const act = basis === "run" ? run : year;
  if (!(act.job_hours > 0)) return fail("No job hours in the chosen window.");
  // Empty or half-read books must stop here, not come out as a low rate.
  if (!(act.revenue > 0) || !(act.wages_books > 0) || !(act.expenses_books > 0) || !(act.overhead > 0)) return fail("The books for this window show no sales, no field wages or no overhead, so a rate cannot be worked out from them.");
  if (!(act.earned_per_job_hour > 0)) return fail("Sales did not cover materials at the markup entered, so a rate cannot be scaled from them. Check the markup under Assumptions.");

  // The rate this window's work was quoted at: quotes written from one month
  // before the window to one month before it ends (work is invoiced after it is quoted).
  const quoted = (inputs && inputs.rates && inputs.rates.quoted) || {};
  const shiftBack = (ym) => { const yy = Number(ym.slice(0, 4)), mm = Number(ym.slice(5, 7)); return mm === 1 ? `${yy - 1}-12` : `${yy}-${String(mm - 1).padStart(2, "0")}`; };
  let qW = 0, qS = 0, qN = 0, qMonths = 0;
  for (const ym of act.months.map(shiftBack)) {
    const q = quoted[ym];
    if (q && num(q.weight) > 0) { qW += num(q.weight); qS += num(q.weighted_rate_sum); qN += num(q.n); qMonths += 1; }
  }
  const vanNow = num(inputs && inputs.rates && inputs.rates.van_now, s.fixed_van_rate) || s.fixed_van_rate;
  const refFromQuotes = qN >= 10 && qW > 0;
  let refVan = refFromQuotes ? qS / qW : vanNow;
  const noData = act.months.length - qMonths;
  const earlier = s.reference_rate_before_data;
  if (refFromQuotes && noData > 0 && isNum(earlier)) {
    // months with no quote data are taken at the rate the admin says was in force then
    refVan = (refVan * qMonths + earlier * noData) / act.months.length;
    assumptions.push(`The work in this window is taken to have been quoted at $${Math.round(refVan)} a van-hour: ${qN} quotes from ${qMonths} of its ${act.months.length} months, and $${Math.round(earlier)} for the months with no quote data (set under Assumptions).`);
  } else if (!refFromQuotes) {
    assumptions.push(`Fewer than ten quotes were written around this window, so the work is taken to have been quoted at today's rate ($${Math.round(vanNow)} a van-hour).`);
  } else if (noData > 0) {
    assumptions.push(`The work in this window is taken to have been quoted at $${Math.round(refVan)} a van-hour, from ${qN} quotes written in ${qMonths} of its ${act.months.length} months. There is no quote data for the other ${noData}. If that work was quoted lower, every rate here is too high by the same share ($10 lower means about 4% lower); enter the earlier rate under Assumptions to correct it.`);
  }
  // What quotes are actually going out at now (the newest complete month and the month in progress).
  let rW = 0, rS = 0, rN = 0;
  for (const ym of currentMonth ? [shiftBack(currentMonth), currentMonth] : []) {
    const q = quoted[ym];
    if (q && num(q.weight) > 0) { rW += num(q.weight); rS += num(q.weighted_rate_sum); rN += num(q.n); }
  }
  const recentRate = rN >= 5 && rW > 0 ? rS / rW : null;

  // ── Today's crew ──
  // Each person is expected to book what they have averaged since they started
  // (up to 12 months, holidays and slow weeks included). Taking only their busy
  // months would overstate the hours and understate the rate.
  const hist = usable.slice(-12);
  const last3 = usable.slice(-3);
  const newestUsable = usable[usable.length - 1];
  const recentMonths = currentMonth && hourMonths.includes(currentMonth) ? [newestUsable, currentMonth] : [newestUsable];
  const today = dayNumber(asOf);
  const crewRows = [], skipped = [];
  for (const p of people) {
    if (!p.active || !isFieldTrade(p.trade)) continue;
    const clocked = hist.filter((ym) => cell(p, ym, P_PAID) > 0);
    const everClocked = clocked.length > 0 || cell(p, currentMonth, P_PAID) > 0;
    if (!everClocked) { skipped.push({ id: p.id, name: p.name, reason: "has not clocked any hours yet" }); continue; }
    if (!(sum(recentMonths.map((ym) => cell(p, ym, P_PAID))) > 0)) {
      // active in Jobber with a field title but not clocking: an old login, or someone on leave
      skipped.push({ id: p.id, name: p.name, reason: "no hours clocked in the last two months" });
      continue;
    }
    // Their months here run from the first month with 15 or more clocked days.
    // Someone with clocked months but never a 15-day one is a casual: measured as
    // they are. Only a person whose first clock is within the last 60 days and who
    // has no full month yet is a new starter.
    let start = hist.findIndex((ym) => cell(p, ym, P_DAYS) >= 15);
    let partTime = false;
    const firstDay = dayNumber(p.first);
    const startedLately = firstDay !== null && today !== null ? today - firstDay <= 60 : clocked.length <= 1;
    if (start < 0 && clocked.length && !startedLately) { start = hist.indexOf(clocked[0]); partTime = true; }
    const own = start >= 0 ? hist.slice(start) : [];
    const recent = last3.filter((ym) => cell(p, ym, P_PAID) > 0);
    crewRows.push({
      id: p.id, name: p.name, trade: p.trade, months_measured: own.length, part_time: partTime,
      measured_paid_hours: own.length ? avg(own.map((ym) => cell(p, ym, P_PAID))) : null,
      measured_job_hours: own.length ? avg(own.map((ym) => cell(p, ym, P_JOB))) : null,
      recent_job_hours: recent.length ? avg(recent.map((ym) => cell(p, ym, P_JOB))) : null,
    });
  }
  // The crew norm: what an established person who is on the tools books in a
  // month. A plain average of them, so one person crossing a line cannot swing it.
  const established = crewRows.filter((r) => r.months_measured >= 6 && !r.part_time && r.measured_paid_hours > 0 && r.measured_job_hours / r.measured_paid_hours >= 0.75);
  const normJob = established.length ? avg(established.map((r) => r.measured_job_hours)) : std * 0.85;
  const normPaid = established.length ? avg(established.map((r) => r.measured_paid_hours)) : std * 0.92;
  const pooledShare = established.length ? clamp(sum(established.map((r) => r.measured_job_hours)) / sum(established.map((r) => r.measured_paid_hours)), 0.5, 1) : 0.92;
  const BLEND = 6;
  let fwdJobHours = 0, fwdPaidWeighted = 0, fwdEarn = 0, fwdAppHours = 0;
  const starters = [];
  for (const r of crewRows) {
    const n = r.months_measured;
    const onTools = n > 0 && r.measured_paid_hours > 0 && r.measured_job_hours / r.measured_paid_hours >= 0.5;
    let autoJob, autoPaid;
    if (n === 0) {
      // Paid in full from day one, but only half the usual job hours are counted
      // until the timesheets show a full month: a new person's hours do not turn
      // into sales at the crew's rate straight away.
      r.status = "new"; autoJob = normJob * NEW_HOURS_FACTOR; autoPaid = normPaid; starters.push(r.name);
    } else if (n < BLEND && onTools && !r.part_time) {
      r.status = "short";
      autoJob = (n * r.measured_job_hours + (BLEND - n) * normJob) / BLEND;
      autoPaid = (n * r.measured_paid_hours + (BLEND - n) * normPaid) / BLEND;
    } else { r.status = r.part_time ? "part_time" : "measured"; autoJob = r.measured_job_hours; autoPaid = r.measured_paid_hours; }
    r.auto_job_hours = autoJob;
    r.auto_share_pct = (autoJob / std) * 100;          // share of a full month spent on jobs
    const ov = s.people[r.id];
    r.plan_share_pct = ov ? ov.tools_pct : null;
    r.job_hours = ov ? (std * ov.tools_pct) / 100 : autoJob;
    // a plan above the hours the person is paid for means they are paid for more
    r.paid_hours = Math.max(autoPaid, r.job_hours);
    // pay sits in field wages unless the person is measured as mostly in the office
    r.in_wage_base = n === 0 || onTools;
    fwdJobHours += r.job_hours;
    fwdEarn += r.job_hours * earnWeight(r.trade);
    if (r.trade === "apprentice") fwdAppHours += r.job_hours;
    if (r.in_wage_base) fwdPaidWeighted += r.paid_hours * tradeWeight(r.trade, k);
  }
  if (!(fwdJobHours > 0)) return fail("Nobody active is clocking job hours.");
  const rosterIds = new Set(crewRows.map((r) => r.id));
  const left = people
    .filter((p) => !rosterIds.has(p.id) && p.trade !== "office")
    .map((p) => ({ id: p.id, name: p.name, job_hours: avg(act.months.map((ym) => cell(p, ym, P_JOB))) }))
    .filter((p) => p.job_hours >= 1)
    .sort((a, b) => b.job_hours - a.job_hours);

  // Field wages for today's crew: the real cost per paid hour (apprentice hours
  // weighted lighter), times the hours today's crew is paid for. Always priced off
  // the 12-month window: three months hold six or seven fortnightly pay runs,
  // which moves a quarter's wages by about 8% either way.
  const wageBasis = yearWin.length >= 12 ? year : act;
  const wageScale = wageBasis.paid_weighted > 0 && fwdPaidWeighted > 0 ? fwdPaidWeighted / wageBasis.paid_weighted : 1;
  if (!(wageBasis.paid_weighted > 0)) warnings.push("Could not match field wages to paid hours, so wages were not adjusted for crew changes.");
  const wagePerWeightedHour = wageBasis.paid_weighted > 0 ? wageBasis.wages_books / wageBasis.paid_weighted : null;
  let fwdWages = wageBasis.wages_books * wageScale;

  // Overhead at the running rate. An account that posted in each of the last
  // three months is taken at its three-month average. Anything lumpier (WorkSafe,
  // accounting, annual licences) at its 12-month average. An account that used to
  // post most months and has posted nothing in the last three has ended (a lease
  // that was replaced) and is left out.
  let fwdOverheadBooked = 0;
  const overheadByCat = {};
  const overheadLines = [];
  const irregular = [], ended = [];
  const before = yearWin.filter((ym) => !runWin.includes(ym));
  const postedIn = (a, win) => win.filter((ym) => Math.abs(num(a.v && a.v[idx[ym]])) > 0.005).length;
  for (const a of overheadAccounts) {
    if (a.cat === "tax" || a.cat === "wear") continue;
    const posted = postedIn(a, runWin);
    if (posted === 0 && before.length >= 6 && postedIn(a, before) >= Math.ceil(before.length * 0.55)) {
      const was = acctAvg(a, yearWin);
      if (Math.abs(was) >= 50) ended.push({ name: a.name, was });
      continue;
    }
    const monthly = posted === runWin.length ? acctAvg(a, runWin) : acctAvg(a, yearWin);
    if (Math.abs(monthly) < 0.005) continue;
    fwdOverheadBooked += monthly;
    overheadByCat[a.cat] = (overheadByCat[a.cat] || 0) + monthly;
    overheadLines.push({ name: a.name, cat: a.cat, monthly, steady: posted === runWin.length });
    // A cost that appeared only in the newest month may be new and monthly from
    // here on. It is counted at its 12-month average, so say so.
    const newestYm = runWin[runWin.length - 1];
    const newest = num(a.v && a.v[idx[newestYm]]);
    if (posted === 1 && a.cat !== "burden" && newest >= 1000 && newest > monthly * 1.5) irregular.push({ name: a.name, posted: newest, counted: monthly, month: newestYm });
  }
  irregular.sort((x, z) => (z.posted - z.counted) - (x.posted - x.counted));
  for (const it of irregular.slice(0, 3)) warnings.push(`${it.name} posted ${dollars(it.posted)} in ${monthName(it.month)} after nothing in the two months before, so it is counted at its 12-month average of ${dollars(it.counted)} a month. If it is now a monthly cost, add the difference under planned costs.`);
  const runWages = catAvg("wages", runWin, ["COGS"]);
  const fieldBurdenRun = burdenRate * runWages;
  overheadByCat.burden = (overheadByCat.burden || 0) - fieldBurdenRun;
  if (wearMonthly) overheadByCat.wear = wearMonthly;
  let fwdOverhead = fwdOverheadBooked - fieldBurdenRun + wearMonthly;

  // The plan. A planned field hire is treated like a new starter: paid in full
  // (with holiday and statutory pay on top of the hourly wage), half the job hours.
  let plannedWages = 0, plannedOverhead = 0, plannedHours = 0;
  const plannedRows = [];
  for (const p of s.planned) {
    if (!p.enabled) { plannedRows.push({ ...p, monthly_total: 0, job_hours: 0, job_hours_full: 0 }); continue; }
    if (p.kind === "field_hire") {
      const wages = p.wage * p.hours_per_month * (1 + PLANNED_PAY_LOADING);
      const full = (p.hours_per_month * p.tools_pct) / 100;
      const jobH = full * NEW_HOURS_FACTOR;
      plannedWages += wages; plannedOverhead += p.monthly_cost; plannedHours += jobH;
      fwdEarn += jobH * earnWeight(p.trade);
      if (p.trade === "apprentice") fwdAppHours += jobH;
      plannedRows.push({ ...p, monthly_total: wages * (1 + burdenRate) + p.monthly_cost, job_hours: jobH, job_hours_full: full });
    } else {
      plannedOverhead += p.monthly_cost;
      plannedRows.push({ ...p, monthly_total: p.monthly_cost, job_hours: 0, job_hours_full: 0 });
    }
  }
  fwdWages += plannedWages;
  fwdOverhead += plannedOverhead;
  fwdJobHours += plannedHours;
  const fwdWagesLoaded = fwdWages * (1 + burdenRate);
  const costFwd = (fwdWagesLoaded + fwdOverhead) / fwdJobHours;

  // ── Rates ──
  // An hour from today's crew earns what the window's hours earned, adjusted for
  // the mix: a crew with more apprentice hours brings in less per hour.
  const pt = act.passthrough_per_job_hour;
  const yAct = act.earned_per_job_hour;
  const mixFactor = act.earn_mix > 0 ? (fwdEarn / fwdJobHours) / act.earn_mix : 1;
  const y = yAct * mixFactor;
  const neededAt = (pct, cost = costFwd, ptH = pt, markup = mk) => (cost + ptH) / (1 - pct / 100) - ptH * (1 + markup);
  const vanAt = (pct) => (refVan * neededAt(pct)) / y;
  const ladderPcts = [...new Set([...LADDER_PCTS, Math.round(targetPct * 10) / 10])].sort((a, b) => a - b);
  const ladder = ladderPcts.map((pct) => {
    const need = neededAt(pct), u = need / y;
    return {
      margin_pct: pct, per_job_hour: need, uplift: u,
      van: refVan * u, van_fully_billed: need * MAN_HOURS_PER_VAN_HOUR,
      tm_jman: isNum(tm.jman) ? tm.jman * u : null, tm_app: isNum(tm.app) ? tm.app * u : null,
      is_target: Math.abs(pct - targetPct) < 0.05,
    };
  });
  const marginAtVan = (v) => {
    if (!isNum(v)) return null;
    const earned = (y * v) / refVan;
    const denom = earned + pt * (1 + mk);
    return denom > 0 ? (1 - (costFwd + pt) / denom) * 100 : null;
  };
  const targetVan = vanAt(targetPct);
  const yAlt = (markup) => ((act.revenue - act.passthrough * (1 + markup)) / act.job_hours) * mixFactor;
  const vanWithMarkup = (markup) => { const ya = yAlt(markup); return ya > 0 ? (refVan * neededAt(targetPct, costFwd, pt, markup)) / ya : null; };
  const vanWithCost = (cost) => (refVan * neededAt(targetPct, cost)) / y;

  // How firm is it? Leave each month of the window out in turn and see how far the rate moves.
  let rangeLow = targetVan, rangeHigh = targetVan;
  if (act.months.length >= 6) {
    for (const drop of act.months) {
      const a = actualFor(act.months.filter((ym) => ym !== drop));
      if (!(a.earned_per_job_hour > 0)) continue;
      const v = (refVan * neededAt(targetPct, costFwd, a.passthrough_per_job_hour)) / (a.earned_per_job_hour * (a.earn_mix > 0 ? (fwdEarn / fwdJobHours) / a.earn_mix : 1));
      if (v < rangeLow) rangeLow = v;
      if (v > rangeHigh) rangeHigh = v;
    }
  }

  const monthly = yearWin.map((ym) => {
    const a = actualFor([ym]);
    const at = usable.indexOf(ym);
    const roll = at >= 2 ? actualFor(usable.slice(at - 2, at + 1)) : null;
    return {
      ym, job_hours: a.job_hours, cost_per_job_hour: a.cost_per_job_hour, earned_per_job_hour: a.earned_per_job_hour, revenue: a.revenue, net: a.net,
      cost_3mo: roll ? roll.cost_per_job_hour : null, earned_3mo: roll ? roll.earned_per_job_hour : null,
    };
  });
  let best = null;
  for (const ym of usable) { const rev = num(revenueByMonth[idx[ym]]); if (!best || rev > best.revenue) best = { ym, revenue: rev }; }

  // The rate kept for quoting is always worked out on the 12-month window: three
  // months swing with when supplier bills land.
  let recommended = { van_rate_raw: targetVan, van_rate: candidateVanRate(targetVan, s), basis };
  if (basis === "run") {
    const yr = computeSellRate(inputs, rawSettings, { ...opts, basis: "year" });
    if (yr.ok) recommended = yr.recommended;
  }
  recommended.clamped = isNum(recommended.van_rate) && isNum(recommended.van_rate_raw) && recommended.van_rate !== roundUpToStep(recommended.van_rate_raw, s.rounding_step);
  const crewsMeasured = hours.crews || null;
  const salesNeededTarget = ((costFwd + pt) * fwdJobHours) / (1 - targetPct / 100);

  assumptions.push(`Materials, subcontractors and permits are taken to be sold at ${s.passthrough_markup_pct}% over cost on average. QuickBooks has one sales account, so this cannot be read from the books. It barely moves the rate for a profit target; it moves the break-even more.`);
  assumptions.push(`The crew is taken to stay as busy as it has been: an established person books about ${Math.round(normJob)} hours a month to jobs (${Math.round(pooledShare * 100)}% of the hours they are paid), holidays included${established.length ? `; that figure is the average of ${established.map((r) => r.name.split(" ")[0]).join(", ")}` : ""}. People here under six months are blended with it.`);
  if (starters.length) assumptions.push(`${starters.join(" and ")} ${starters.length === 1 ? "is" : "are"} new. A new person is paid in full but counted at half the usual job hours until the timesheets show a full month.`);
  if (Math.abs(mixFactor - 1) >= 0.005) assumptions.push(`Today's crew is ${Math.round((fwdAppHours / fwdJobHours) * 100)}% apprentice hours against ${Math.round(act.apprentice_share_pct)}% in the window. An apprentice hour is taken to earn ${Math.round(earnApp * 100)}% of a journeyman hour (the T&M rates), so each hour from today's crew is taken to bring in ${Math.abs(Math.round((mixFactor - 1) * 100))}% ${mixFactor < 1 ? "less" : "more"} than the window's.`);
  assumptions.push(`An apprentice hour is taken to cost ${Math.round(k * 100)}% of a journeyman hour when field wages are scaled to today's crew.`);
  assumptions.push("A three-month window holds six or seven fortnightly pay runs, so its wages can read about 8% high or low. Field wages for today's crew are priced from the 12-month window in both views, and the rate kept for quoting always uses 12 months.");
  assumptions.push("Hours booked to jobs are counted, not hours invoiced. Travel and overruns on quoted jobs are inside them, which is why the rate is scaled from what the work actually earned.");
  assumptions.push("The rate assumes all labour is priced up together. Job-type exceptions and the T&M rates are not moved by this page; if they stay where they are, the standard rate has to be higher to reach the same profit.");
  if (salesNeededTarget > num(best && best.revenue)) warnings.push(`Reaching ${targetPct}% with today's crew and costs takes about ${dollars(salesNeededTarget)} of sales a month. The best month in the books is ${dollars(best.revenue)} (${monthName(best.ym)}); the last three averaged ${dollars(run.revenue)}.`);
  const inventory = num(inputs && inputs.hints && inputs.hints.inventory_balance);
  if (inventory >= 5000) warnings.push(`QuickBooks shows about ${dollars(inventory)} of stock on hand. Its cost only reaches these numbers when it is moved to job materials. If the jobs that use it are invoiced first, those months look more profitable than they were and the rate reads low.`);
  if (ended.length) assumptions.push(`Left out as ended (nothing posted in three months after posting most months before): ${ended.slice(0, 6).map((e) => e.name).join("; ")}.`);
  const idle = skipped.filter((x) => /two months/.test(x.reason));
  const notStarted = skipped.filter((x) => !/two months/.test(x.reason));
  if (idle.length) warnings.push(`${idle.map((x) => x.name).join(", ")} ${idle.length === 1 ? "is" : "are"} active in Jobber with a field title but ${idle.length === 1 ? "has" : "have"} not clocked in two months, and ${idle.length === 1 ? "is" : "are"} left out of the crew.`);
  if (notStarted.length) warnings.push(`${notStarted.map((x) => x.name).join(", ")} ${notStarted.length === 1 ? "has" : "have"} a field title in Jobber but no hours yet, and ${notStarted.length === 1 ? "is" : "are"} not counted until the first clock-in.`);

  const vans = fwdJobHours / (MAN_HOURS_PER_VAN_HOUR * normJob);
  return {
    ok: true, errors: [], warnings, assumptions,
    basis, target_margin_pct: targetPct,
    settings: s,
    burden_rate_pct: burdenRate * 100,
    wear: { monthly: wearMonthly, source: wearSource, last_booked: lastBooked || null },
    actual: { year, run, used: basis, best_month: best },
    reference: {
      van_rate: refVan, from_quotes: refFromQuotes, quotes: qN, months_with_quotes: qMonths, months_without_quotes: noData,
      van_now: vanNow, yield_pct: (yAct * MAN_HOURS_PER_VAN_HOUR) / refVan * 100,
      recent_rate: recentRate, recent_quotes: rN,
    },
    crew: {
      people: crewRows.sort((a, b) => (a.trade === b.trade ? b.job_hours - a.job_hours : a.trade === "jman" ? -1 : 1)),
      left, skipped,
      job_hours: fwdJobHours - plannedHours, pooled_share_pct: pooledShare * 100,
      norm_job_hours: normJob, norm_paid_hours: normPaid, norm_from: established.map((r) => r.name),
      wage_per_paid_hour: wagePerWeightedHour,   // per journeyman-equivalent paid hour
      full_month_job_hours: normJob,
      vans_billable: vans,
      heads: crewRows.length, measured: crewsMeasured,
      wage_scale: wageScale,
      apprentice_share_pct: (fwdAppHours / fwdJobHours) * 100, mix_factor: mixFactor,
    },
    planned: { rows: plannedRows, wages: plannedWages, overhead: plannedOverhead, job_hours: plannedHours },
    forward: {
      wages: fwdWages, wages_loaded: fwdWagesLoaded, overhead: fwdOverhead, job_hours: fwdJobHours,
      wage_per_job_hour: fwdWagesLoaded / fwdJobHours, overhead_per_job_hour: fwdOverhead / fwdJobHours, cost_per_job_hour: costFwd,
      monthly_cost: fwdWagesLoaded + fwdOverhead,
      overhead_by_cat: overheadByCat,
      overhead_lines: overheadLines.sort((a, b) => b.monthly - a.monthly).slice(0, 40),
      overhead_ended: ended,
      sales_needed: { break_even: (costFwd + pt) * fwdJobHours, target: salesNeededTarget },
    },
    rates: {
      passthrough_per_job_hour: pt, earned_per_job_hour: yAct, earned_per_job_hour_today: y,
      needed_per_job_hour: { break_even: neededAt(0), target: neededAt(targetPct) },
      van: {
        break_even: vanAt(0), target: targetVan, target_low: rangeLow, target_high: rangeHigh,
        now: vanNow, margin_at_now_pct: marginAtVan(vanNow),
        recent: recentRate, margin_at_recent_pct: marginAtVan(recentRate),
      },
      van_fully_billed: { break_even: neededAt(0) * MAN_HOURS_PER_VAN_HOUR, target: neededAt(targetPct) * MAN_HOURS_PER_VAN_HOUR },
      tm: { jman_now: isNum(tm.jman) ? tm.jman : null, app_now: isNum(tm.app) ? tm.app : null, dispatch_now: isNum(tm.dispatch) ? tm.dispatch : null },
      ladder,
      sensitivity: {
        markup_minus_5: vanWithMarkup(Math.max(0, mk - 0.05)), markup_plus_5: vanWithMarkup(mk + 0.05),
        per_1000_overhead: vanWithCost(costFwd + 1000 / fwdJobHours) - targetVan,
        per_50_job_hours_lost: fwdJobHours > 60 ? vanWithCost((fwdWagesLoaded + fwdOverhead) / (fwdJobHours - 50)) - targetVan : null,
      },
    },
    recommended,
    monthly,
    summary: {
      break_even_van: r2(vanAt(0)), target_van: r2(targetVan), target_margin_pct: targetPct, basis,
      cost_per_job_hour: r2(costFwd), earned_per_job_hour: r2(y), job_hours: r2(fwdJobHours), vans_billable: r2(vans),
    },
  };
}
